import * as React from "react";
import * as moment from "moment";
import {ProductsEnum, resourcesHelper, Subscription} from "@falcon/services";
import {InfoModal} from "../modals/InfoModal";
import {PricingExplainedInfo} from "../PricingExplainedInfo";
import {getAmountToDisplay} from "../../CommonFunctions";
import {Link} from "react-router-dom";

interface IProps {
    showInfoModal?: boolean;
    subscription: Subscription;
    oldMissedPaymentProcess?: boolean;
}

export class PaymentMessageBanner extends React.Component<IProps> {
    public render() {
        const {subscription, showInfoModal, oldMissedPaymentProcess} = this.props;
        const daysToPay = oldMissedPaymentProcess ? 7 : 3;
        const dateFormat = "D MMMM YYYY";
        const nextPaymentDate: string = moment(subscription.nextPaymentDate).format(dateFormat);
        const secondPaymentDate: string = moment(subscription.renewalStartDate ? subscription.renewalStartDate : subscription.nextPaymentDate).add(daysToPay, "day").format(dateFormat);
        const cancellationDate: string = moment(secondPaymentDate).add(1, "day").format(dateFormat);
        const firstPaymentFailed: boolean = subscription.renewalPaymentFailed;
        const secondPaymentFailed: boolean = subscription.renewalSecondPaymentFailed;
        const paymentTechFail: boolean = subscription.renewalPaymentTechFail;
        const allPoliciesAreCancelled: boolean = subscription.allPoliciesAreCancelled;

        const CLOSURE_WARNING_DATE = process.env.REACT_APP_SUBSCRIPTION_CLOSURE_WARNING_DATE;

        const getClosureWarningDate = () => {
            const date = moment(CLOSURE_WARNING_DATE);
            if (!date.isValid()) {
                throw new Error("Invalid date format. Expected YYYY-MM-DD: " + CLOSURE_WARNING_DATE);
            }
            return date.toDate();
        }

        const showClosureWarning = (CLOSURE_WARNING_DATE === undefined) ? false :
            (new Date() >= getClosureWarningDate());
        const closureDate = (showClosureWarning) ? moment(subscription.subscriptionClosureDate).format(dateFormat) : null;

        return (
            <React.Fragment>
                { showClosureWarning &&
                    <div className = "banner">
                        { this.renderInfoModal() }
                        <div className="banner-text text-red">
                            <b>
                                Your {resourcesHelper.THEME} Monthly insurance will end on {closureDate}, from this date you won't be covered.
                                Visit FAQs for details.
                            </b>
                        </div>
                    </div>
                }
                <div className={`banner ${firstPaymentFailed && "red-border"}`}>
                    {showInfoModal && !firstPaymentFailed && this.renderInfoModal()}
                    {firstPaymentFailed && !secondPaymentFailed && moment().isSameOrBefore(secondPaymentDate, "day") && this.renderPaymentFailed(secondPaymentDate)}
                    {secondPaymentFailed && !allPoliciesAreCancelled && this.renderSecondPaymentFailed(cancellationDate)}
                    {paymentTechFail && <div className="banner-text">Your next payment is due on <b id="next-payment-date">{secondPaymentDate}</b>. {this.renderPaymentAmount(subscription.lastPaymentAmount)}</div>}
                    {!firstPaymentFailed && !paymentTechFail && <div className="banner-text">Your next payment is due on <b id="next-payment-date">{nextPaymentDate}</b>. {this.renderPaymentAmount(subscription.nextPaymentAmount)}</div>}
                </div>
            </React.Fragment>
        );
    }

    private renderInfoModal(): React.ReactNode {
        return (
            <InfoModal title="Pricing explained">
                <PricingExplainedInfo/>
            </InfoModal>
        );
    }

    private renderPaymentAmount(paymentAmount: number): React.ReactNode {
        if (paymentAmount) {
            return (
                <React.Fragment>
                    The amount you'll need to pay is <b id="next-payment-amount">{getAmountToDisplay(paymentAmount)}</b>
                </React.Fragment>
            );
        } else {
            const {subscription} = this.props;
            return (
                <React.Fragment>
                    We will calculate your price on <b id="renewal-window-start-date">{moment(subscription.renewalWindowStartDate).format("D MMMM YYYY")}</b>
                </React.Fragment>
            );
        }
    }

    private renderPaymentFailed(missedFirstPaymentNextPaymentDate: string): React.ReactNode {
        return(
            <React.Fragment>
                <img src="/img/alert-orange.svg" alt="Orange alert icon"/>
                <div className="banner-text">
                    <p>
                        We had a problem taking payment for your Flow insurance subscription this month.
                        But don’t worry, we’ll try to take your payment again on <b>{missedFirstPaymentNextPaymentDate}</b>.
                        If you’d like your cover to continue, please make sure that funds are available when we try to take the payment again.
                    </p>
                    <p>
                        Alternatively, if you click on <Link
                        state={{ verifyCardFailed: false, product: ProductsEnum.SUBSCRIPTION }}
                        to="/dashboard/account/missedPayment">
                        Pay now</Link> you can make a payment to cover your outstanding Flow subscription.
                        Once this payment has been confirmed, your Flow account will be unlocked for you to make changes and your renewal documents will be generated.
                    </p>
                </div>
            </React.Fragment>
        );
    }

    private renderSecondPaymentFailed(cancellationDate: string): React.ReactNode {
        return(
            <React.Fragment>
                <img src="/img/alert-orange.svg" alt="Orange alert icon"/>
                <div className="banner-text">
                    <p>
                        As we couldn't take a payment for your Flow insurance subscription for a second time, we'll have to cancel your cover on <b>{cancellationDate}</b>.<br/>
                        Remember, it's illegal to drive without insurance, so make sure you're covered.
                    </p>
                    <p>
                        Last chance to continue with your subscription: <Link
                        state={{ verifyCardFailed: false, product: ProductsEnum.SUBSCRIPTION }}
                        to="/dashboard/account/missedPayment">
                        Pay now</Link>.
                    </p>
                </div>
            </React.Fragment>
        );
    }

}
